import React from "react";
import { Box, Typography, Card, Stack } from "@mui/material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ReactComponent as Carousel1 } from "../assets/Carousel1.svg"; // Import SVG
import { ReactComponent as Carousel2 } from "../assets/Carousel2.svg"; // Import SVG
import { ReactComponent as Carousel3 } from "../assets/Carousel3.svg"; // Import SVG
import { ReactComponent as Care } from "../assets/Care.svg"; // Import SVG
import { ReactComponent as Consult } from "../assets/Consult.svg"; // Import SVG
import { ReactComponent as Diagnostic } from "../assets/Diagnostic.svg"; // Import SVG
import { ReactComponent as Monitoring } from "../assets/Monitoring.svg"; // Import SVG

const carouselsBg = [
  {
    carousel: (
      <Box
        sx={{ width: "100%", height: { xs: 300, sm: 400, md: 700, lg: 800 } }}
      >
        <Carousel1
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover", // Ensures the SVG fills the entire box
          }}
          preserveAspectRatio="none" // This removes aspect ratio constraints, making it stretch to fit
        />
      </Box>
    ),
    title: "Decode",
  },
  {
    carousel: (
      <Box
        sx={{ width: "100%", height: { xs: 300, sm: 400, md: 700, lg: 800 } }}
      >
        <Carousel2
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover", // Ensures the SVG fills the entire box
          }}
          preserveAspectRatio="none" // This removes aspect ratio constraints, making it stretch to fit
        />
      </Box>
    ),
    title: "Assist",
  },
  {
    carousel: (
      <Box
        sx={{ width: "100%", height: { xs: 300, sm: 400, md: 700, lg: 800 } }}
      >
        <Carousel3
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover", // Ensures the SVG fills the entire box
          }}
          preserveAspectRatio="none" // This removes aspect ratio constraints, making it stretch to fit
        />
      </Box>
    ),
    title: "Transform",
  },
];

export default function SectionOne() {
  const settings = {
    dots: true,
    infinite: true,
    speed: 3500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2700,
    pauseOnHover: false,
    cssEase: "cubic-bezier(0.55, 0.03, 0.24, 1)",
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%", // Full-width box
        backgroundColor: "white", // Background color
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
        pb: {
          xs: "1350px", // Extra padding for smaller screens
          sm: "1000px", // Slightly more for small screens
          md: "750px", // Slightly more for medium screens
          lg: "750px", // More space for larger screens
        }, // Padding to ensure the card doesn't get clipped
      }}
    >
      {/* Slick Carousel */}
      <Slider {...settings}>
        {carouselsBg.map((carousel, index) => (
          <Box
            key={index}
            sx={{
              position: "relative",
              width: "100%",
              m: 0, // Remove margin
              p: 0, // Remove padding
            }}
          >
            {/* SVG Background */}

            {carousel.carousel}

            {/* Centered Text */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: { xs: -50, sm: -50, md: -90 },
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "white", // Adjust text color as needed
                textAlign: "center", // Center-align text
              }}
            >
              <Typography
                fontWeight={"700"}
                fontSize={{
                  xs: "24px", // Smaller font size for extra-small devices
                  sm: "48px", // Medium font size for small devices
                  lg: "96px", // Larger font size for large devices
                }}
                color="#2361DF"
              >
                {carousel.title}
              </Typography>
            </Box>
          </Box>
        ))}
      </Slider>

      {/* Static Card */}
      <Section1Card />
    </Box>
  );
}

const Section1Card = () => {
  return (
    <Card
      sx={{
        position: "absolute",
        top: { xs: "200px", sm: "250px", md: "510px", lg: "600px" }, // Moves the card 50px outside the background box
        left: "50%", // Center horizontally
        transform: "translateX(-50%)", // Adjust for horizontal centering
        width: {
          xs: "80%", // Takes 90% width on extra-small devices
          sm: "80%", // Takes 80% width on small devices
          lg: "80%", // Takes 60% width on large devices
          md: "85%",
        },
        boxShadow: "0px 0px 40px 12px rgba(0, 0, 0, 0.1)", // Box shadow for styling
        borderRadius: "12px", // Rounded corners
        padding: 2, // Internal padding for spacing
      }}
    >
      <Stack spacing={3} p={4}>
        {/* Header and Subheader */}
        <Typography
          textAlign={"center"}
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "20px", // Smaller font size for extra-small devices
              sm: "24px", // Medium font size for small devices
              lg: "48px", // Larger font size for large devices
            },
            color: "black",
            fontFamily: "SF Pro Display",
          }}
        >
          <span style={{ color: "#2361df", fontFamily: "SF Pro Display" }}>
            B
          </span>
          ehind{" "}
          <span style={{ color: "#2361df", fontFamily: "SF Pro Display" }}>
            T
          </span>
          he{" "}
          <span style={{ color: "#2361df", fontFamily: "SF Pro Display" }}>
            S
          </span>
          cenes
        </Typography>
        <Typography
          fontWeight={500}
          fontSize={{
            lg: "20px",
            md: "16px",
            sm: "14px",
            xs: "12px",
          }}
          align="center"
          gutterBottom
          id="intro-app"
          fontFamily={"SF Pro Display"}
          letterSpacing={{ lg: 0, xs: 0.5 }}
        >
          Our AAA algorithm blends ancient wisdom with AI to redefine
          personalised healthcare.
        </Typography>

        {/* Boxes */}
        <Stack spacing={3}>
          <DiagnosticsCard
            icon={Diagnostic}
            header="Diagnostic Precision"
            content="Input your symptoms, and our algorithm will design personalized test recommendations, offering unique health insights derived from Lab Reports, Gut Health, Genetics, and key biomarkers such as Prakriti, Dosha Imbalances, Skin, Hair, Stool, Urine, and Tongue."
          />

          {/* Second row: Two boxes in a flex container */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <DiagnosticsCard
              icon={Consult}
              header="Expert Consultations"
              content="Connect with our expert healthcare professionals for personalized guidance and solutions, available both online and at our advanced AI-enabled clinics."
            />

            <DiagnosticsCard
              icon={Monitoring}
              header="Personalized Care"
              content="Benefit from customized prescriptions and medicines, along with daily dietary suggestions designed to address your unique health needs."
            />
          </Stack>

          {/* Third row: Single box */}
          <DiagnosticsCard
            icon={Care}
            header="Proactive Health Monitoring"
            content="Track and monitor your progress with our AI-powered app, guiding you at every step and empowering you to make informed decisions throughout the day."
          />
        </Stack>
      </Stack>
    </Card>
  );
};

const DiagnosticsCard = ({ icon: Icon, header, content }) => {
  return (
    <Stack
      direction={"row"}
      spacing={2}
      sx={{
        borderRadius: "12px",
        borderWidth: "2px",
        borderColor: "#E6E8EC",
        borderStyle: "solid",
        padding: 2,
      }}
      alignItems={"flex-start"}
    >
      {/* SVG Container */}
      <Box
        sx={{
          width: { xs: "30px", sm: "40px", md: "50px", lg: "60px" }, // Adjust size based on screen
          height: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        mr={2}
        pt={1}
      >
        {/* Dynamically rendered icon */}
        <Icon style={{ width: "30px", height: "30px" }} />
      </Box>

      {/* Content Section */}
      <Stack direction={"column"} spacing={1}>
        {/* Header */}
        <Typography
          fontSize={{
            xs: "14px", // Smaller font size for extra-small devices
            sm: "16px", // Medium font size for small devices
            lg: "24px", // Larger font size for large devices
          }}
          fontWeight={"600"}
          fontFamily={"SF Pro Display"}
        >
          {header}
        </Typography>

        {/* Content / Description */}
        <Typography
          fontSize={{
            lg: "20px", // Larger font size for large devices
            md: "16px", // Medium font size for small devices
            sm: "15px", // Medium font size for small devices
            xs: "14px", // Smaller font size for extra-small devices
          }}
          letterSpacing={{ lg: 0, xs: 0.5 }}
          fontWeight={"400"}
          fontFamily={"SF Pro Display"}
          color="rgba(106, 106, 106, 1)"
        >
          {content}
        </Typography>
      </Stack>
    </Stack>
  );
};
